import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiTrashLight, PiUserListLight, PiProhibit, PiPlusLight, PiUploadLight, PiClockCounterClockwiseLight } from 'react-icons/pi';
import Button from '../shared/TailwindComponents/Button';
import ResourceTrash from '../RecycleBin/ResourceTrash';

import { listClients, deleteClient as deleteClientAPI, bulkDeleteClients, Client } from '../../store/actions/ClientActions';
import ImportClientsWizard from '../Import/ImportClientsWizard';
import DeleteClientsConfirmation from './DeleteClientsConfirmation';
import Header from '../shared/TailwindComponents/Header';
import Badge from '../shared/TailwindComponents/Badge';
import Table from '../shared/TailwindComponents/Table';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import ClientForm from './ClientForm';
import SkeletonClientItem from './SkeletonClientItem';
import apis from '../../utils/apis';
import { useAppDispatch } from '../../store';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { FeatureTour, clientsListTourSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';

const isLocalhost = () => window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const ClientsList: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isMountedRef = useRef(true);

  // Local state only
  const [clients, setClients] = useState<Client[]>([]);
  const [totalClients, setTotalClients] = useState(0);
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [trashBulkActions, setTrashBulkActions] = useState<{ label: string; onClick: () => void; icon?: React.ReactElement }[]>([]);
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);
  const [isClientsLoading, setIsClientsLoading] = useState(false);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [clientTypeMap, setClientTypeMap] = useState<Record<number, string>>({});
  const [hierarchicalClientTypes, setHierarchicalClientTypes] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
  const [isImporting, setIsImporting] = useState(false);
  const [auditTarget, setAuditTarget] = useState<{ type: string; id: string; name: string } | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const clientsPerPage = 50;

  const {
    isRunning: isTourRunning,
    handleJoyrideCallback: handleTourCallback,
  } = useFeatureTour({
    tourId: 'clients-list',
    steps: clientsListTourSteps,
    readyToShow: !isClientsLoading || clients.length > 0,
    autoStartDelay: 600,
  });

  // Build hierarchical client types from flat list
  const buildHierarchicalClientTypes = (flatList: any[]): any[] => {
    const typeMap = new Map<number | string, any>();

    // First pass: create all items
    flatList.forEach((item: any) => {
      typeMap.set(item.id, {
        value: String(item.id),
        label: item.label,
        id: item.id,
        parent_id: item.parent_id,
        sort_order: item.sort_order,
        children: [],
      });
    });

    // Second pass: build hierarchy
    const roots: any[] = [];
    typeMap.forEach((option, itemId) => {
      if (option.parent_id === null) {
        roots.push(option);
      } else {
        const parent = typeMap.get(option.parent_id as number);
        if (parent && parent.children) {
          parent.children.push(option);
        }
      }
    });

    // Sort by sort_order
    roots.sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
    roots.forEach((parent: any) => {
      if (parent.children) {
        parent.children.sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
      }
    });

    return roots;
  };

  // Sync URL with isAddingClient state
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('add') === 'true') {
      setIsAddingClient(true);
    } else {
      setIsAddingClient(false);
    }
  }, [location]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch client types from dropdown
  useEffect(() => {
    const fetchClientTypes = async () => {
      try {
        const response = await apis.get('/dropdown/client-types');
        if (response && response.data) {
          let typeList = Array.isArray(response.data) ? response.data : response.data.data || response.data.list || [];

          // Build flat map for display purposes
          const typeMap: Record<number, string> = {};
          typeList.forEach((item: any) => {
            const itemId = item.id;
            const itemName = item.label;
            if (itemId && itemName) {
              const numId = typeof itemId === 'string' ? parseInt(itemId, 10) : itemId;
              if (!isNaN(numId)) {
                typeMap[numId] = itemName;
              }
            }
          });

          if (Object.keys(typeMap).length > 0) {
            setClientTypeMap(typeMap);
          }

          // Build hierarchical structure for filters
          const hierarchical = buildHierarchicalClientTypes(typeList);
          setHierarchicalClientTypes(hierarchical);
        }
      } catch (err) {
        console.error('Error fetching client types:', err);
      }
    };

    fetchClientTypes();
  }, []);

  // Fetch clients (page 1, replaces list) — extracted to a memoized function so it can be reused
  const fetchClients = useCallback(async (sort_by?: string, sort_order?: 'asc' | 'desc', filters?: Record<string, string[]>) => {
    try {
      setIsClientsLoading(true);
      setError(null);
      setPage(1);
      setSelectedClients([]);

      const response = await listClients(1, clientsPerPage, {
        sort_by: sort_by || sortBy,
        sort_order: sort_order || sortOrder,
        filters: filters || selectedFilters,
      });

      if (response && response.success && Array.isArray(response.clients)) {
        setClients(response.clients);
        const total = response.pagination?.total || 0;
        const totalPages = response.pagination?.total_pages || Math.ceil(total / clientsPerPage);
        setTotalClients(total);
        setHasMore(1 < totalPages && response.clients.length >= clientsPerPage);
      } else {
        throw new Error(`Invalid response: success=${response?.success}, clients=${Array.isArray(response?.clients)}`);
      }
    } catch (err: any) {
      if (!isMountedRef.current) return;
      const errorMsg = err?.message || 'Failed to fetch clients';
      setError(errorMsg);
      setClients([]);
      setTotalClients(0);
      setHasMore(false);
    } finally {
      setIsClientsLoading(false);
    }
  }, [sortBy, sortOrder, selectedFilters]);

  // Load next page of clients (appends to current list)
  const loadMoreClients = useCallback(async () => {
    if (!hasMore || isClientsLoading) return;
    try {
      setIsClientsLoading(true);
      const nextPage = page + 1;

      const response = await listClients(nextPage, clientsPerPage, {
        sort_by: sortBy,
        sort_order: sortOrder,
        filters: selectedFilters,
      });

      if (response && response.success && Array.isArray(response.clients)) {
        setClients(prev => [...prev, ...response.clients]);
        setPage(nextPage);
        const total = response.pagination?.total || 0;
        const totalPages = response.pagination?.total_pages || Math.ceil(total / clientsPerPage);
        setTotalClients(total);
        setHasMore(nextPage < totalPages && response.clients.length >= clientsPerPage);
      }
    } catch (err: any) {
      if (!isMountedRef.current) return;
      setError(err?.message || 'Failed to load more clients');
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsClientsLoading(false);
    }
  }, [hasMore, isClientsLoading, page, sortBy, sortOrder, selectedFilters]);

  // Fetch clients on mount
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Handle client creation - navigate to client detail page
  const handleClientCreated = useCallback(async (newClient: any) => {
    // Navigate to the new client's detail page
    if (newClient && newClient.uuid) {
      history.push(`/contacts/${newClient.uuid}`);
    } else {
      // Fallback if uuid is not available
      await fetchClients();
      history.push('/contacts');
    }
  }, [fetchClients, history]);

  // Handle delete
  const handleDeleteClient = (clientId: string | number) => {
    const client = clients.find((c) => String(c.id) === String(clientId));
    if (!client) return;

    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Delete Client'),
        subMsg: t(`This will permanently delete "${client.name}". This action cannot be undone.`),
        isDest: true,
        onConfirm: async () => {
          try {
            await deleteClientAPI(client.uuid);

            if (!isMountedRef.current) return;

            // Re-fetch the entire list to ensure consistency
            await fetchClients();

            // Close the modal
            dispatch(hideModal());

          dispatch(
            showFeedbackModal({
              showModal: true,
              message: t('Client deleted successfully'),
              modalType: 'success',
              duration: 3000,
            })
          );
          } catch (err: any) {
            if (!isMountedRef.current) return;

            const errorMsg = err?.message || t('Failed to delete client');
            setError(errorMsg);
            setTimeout(() => setError(null), 5000);
          }
        },
        onCancel: () => {
          dispatch(hideModal());
        },
      } as any)
    );
  };

  // Handle bulk deleting selected clients — single API call with red spinner
  const handleBulkDeleteClients = () => {
    if (selectedClients.length === 0) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'warning',
        message: t('Please select at least one client'),
        duration: 3000,
      }));
      return;
    }

    const clientUuids = clients
      .filter((c) => selectedClients.includes(String(c.id)))
      .map((c) => c.uuid);

    dispatch(
      showModal({
        showModal: true,
        type: 'custom',
        message: t(clientUuids.length > 1 ? 'Delete Clients' : 'Delete Client'),
        subMsg: '',
        icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
        content: (
          <DeleteClientsConfirmation
            clientCount={clientUuids.length}
            onConfirm={async () => {
              try {
                const result = await bulkDeleteClients(clientUuids);
                if (result && result.deleted_count > 0) {
                  await fetchClients();
                  setSelectedClients([]);
                  dispatch(showFeedbackModal({
                    message: t('{{count}} client(s) deleted successfully', { count: result.deleted_count }),
                    modalType: result.failed_count > 0 ? 'warning' : 'success',
                    duration: 3000,
                    showModal: true,
                  }));
                }
                if (result?.failed_count > 0) {
                  setError(t('Failed to delete {{count}} client(s)', { count: result.failed_count }));
                  setTimeout(() => setError(null), 5000);
                }
              } catch (error: any) {
                setError(error.message || t('Failed to delete clients'));
                setTimeout(() => setError(null), 5000);
              }
              dispatch(hideModal());
            }}
            onCancel={() => dispatch(hideModal())}
          />
        ),
        onCancel: () => {},
      })
    );
  };

  // Handle deactivating selected clients
  const handleDeactivateClients = () => {
    if (selectedClients.length === 0) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'warning',
        message: t('Please select at least one client'),
        duration: 3000,
      }));
      return;
    }

    const count = selectedClients.length;

    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Deactivate Clients'),
        subMsg: t(`Are you sure you want to deactivate ${count} selected client(s)? They will no longer appear as active.`),
        icon: <PiProhibit className="w-6 h-6 text-red-600" />,
        isDest: true,
        onConfirm: async () => {
          try {
            const clientsToDeactivate = clients.filter((c) =>
              selectedClients.includes(String(c.id))
            );

            const results = await Promise.allSettled(
              clientsToDeactivate.map((client) =>
                apis.put(`/clients/${client.uuid}`, { is_active: false })
              )
            );

            const failed = results.filter((r) => r.status === 'rejected').length;
            const succeeded = results.filter((r) => r.status === 'fulfilled').length;

            if (succeeded > 0) {
              setClients((prev) =>
                prev.map((c) =>
                  selectedClients.includes(String(c.id)) ? { ...c, is_active: false } : c
                )
              );
              dispatch(showFeedbackModal({
                message: t(`${succeeded} client(s) deactivated successfully`),
                modalType: 'success',
                duration: 3000,
                showModal: true,
              }));
              setSelectedClients([]);
            }

            if (failed > 0) {
              setError(t(`Failed to deactivate ${failed} client(s)`));
              setTimeout(() => setError(null), 5000);
            }
          } catch (error: any) {
            setError(error.message || t('Failed to deactivate clients'));
            setTimeout(() => setError(null), 5000);
          }
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      })
    );
  };

  // Handle selecting/deselecting a single client
  const handleSelectItem = (id: string) => {
    setSelectedClients((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Handle selecting/deselecting all clients
  const handleSelectAllItems = () => {
    const allClientIds = clients.map((c) => String(c.id));
    const allSelected = selectedClients.length === allClientIds.length;
    setSelectedClients(allSelected ? [] : allClientIds);
  };

  // Handle sorting
  const handleSort = useCallback((column: string) => {
    let newSortOrder: 'asc' | 'desc' = 'asc';

    // If clicking the same column, toggle the sort order
    if (sortBy === column) {
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }

    setSortBy(column);
    setSortOrder(newSortOrder);

    // Fetch clients with new sort parameters
    fetchClients(column, newSortOrder);
  }, [sortBy, sortOrder, fetchClients]);

  // Handle filter changes
  const handleFilterChange = useCallback((filterSection: string, updatedFilters: string[]) => {
    const newSelectedFilters = { ...selectedFilters };

    if (updatedFilters.length > 0) {
      newSelectedFilters[filterSection] = updatedFilters;
    } else {
      delete newSelectedFilters[filterSection];
    }

    setSelectedFilters(newSelectedFilters);

    // Fetch clients with new filter parameters
    fetchClients(sortBy, sortOrder, newSelectedFilters);
  }, [selectedFilters, sortBy, sortOrder, fetchClients]);

  // Filter actions that appear when items are selected
  const filterActions = [
    ...(selectedClients.length > 0 ? [
      {
        label: t('Deactivate'),
        onClick: handleDeactivateClients,
        icon: <PiProhibit className="h-4 w-4" />,
      },
      {
        label: t('Delete'),
        onClick: handleBulkDeleteClients,
        icon: <PiTrashLight className="h-4 w-4" />,
      },
    ] : []),
  ];


  // Prepare table data
  const tableData = clients.map((client: any) => {
    const isIndividual = client.is_individual !== false; // Default to individual if not set
    const contactPersonName = client.contact_person_name || '';
    const subtitle = isIndividual
      ? (client.contact_email || '')
      : (contactPersonName ? `${t('Contact')}: ${contactPersonName}` : (client.contact_email || ''));

    return {
      id: String(client.id),
      uuid: client.uuid,
      Client: {
        name: client.name,
        email: subtitle,
        clientType: clientTypeMap[client.type_id] || `Type ${client.type_id}`,
        companyLogo: null,
      },
      'Client Type': clientTypeMap[client.type_id] || `Type ${client.type_id}`,
      Matters: client.matters_count || 0,
      'Is Onboarded': <Badge color={client.is_onboarded ? 'green' : 'yellow'}>{client.is_onboarded ? t('Yes') : t('No')}</Badge>,
      Status: <Badge color={client.is_active ? 'green' : 'gray'}>{client.is_active ? t('Active') : t('Inactive')}</Badge>,
      name: client.name,
      client_type: clientTypeMap[client.type_id] || `Type ${client.type_id}`,
      case_count: client.matters_count || 0,
      total_value: 0,
      className: selectedClients.includes(String(client.id))
        ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
        : '',
    };
  });

  const filters = [
    {
      id: 'type',
      name: t('Type'),
      options: hierarchicalClientTypes,
    },
  ];

  const columns = ['Client', 'Client Type', 'Matters', 'Is Onboarded', 'Status', 'Actions'];

  const sortableColumns = ['Client', 'Matters'];

  return (
    <div className="bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark transition-colors duration-300 flex flex-col h-full overflow-hidden">
      {/* Error message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 m-4 rounded">
          {error}
        </div>
      )}

      <div className="transition-all duration-200 bg-background-light dark:bg-background-dark flex-1 flex flex-col">
        {!isAddingClient && (
          <>
            <Header
              actions={[
                // Import is only available on localhost until fully ready
                ...(isLocalhost() ? [{
                  label: t('Import Clients'),
                  icon: <PiUploadLight className='w-4 h-4' />,
                  onClick: () => setIsImporting(true),
                  variant: 'outline' as const,
                }] : []),
                {
                  label: t('Add Client'),
                  icon: <PiPlusLight className='w-4 h-4' />,
                  onClick: () => history.push('/contacts?add=true'),
                  requiresSubscription: true,
                  variant: 'primary' as const,
                },
              ]}
            />
            <div data-tour-id="clients-toolbar">
            <FilterBarWithTabs
              filters={filters}
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
              onSearchChange={() => {}}
              onClearAllFilters={() => {
                setSelectedFilters({});
                fetchClients(sortBy, sortOrder, {});
              }}
              actions={[
                ...filterActions,
                ...(showTrash ? trashBulkActions : []),
              ]}
              selectionCount={showTrash ? trashSelectedCount : (selectedClients.length > 0 ? selectedClients.length : undefined)}
              recycleBinEnabled
              recycleBinActive={showTrash}
              recycleBinLabel={t('Contacts')}
              onRecycleBinToggle={(active) => { if (!active) fetchClients(sortBy, sortOrder, selectedFilters); setShowTrash(active); }}
            />
            </div>
          </>
        )}

        {showTrash ? (
          <ResourceTrash
            entityType="client"
            entityLabel="clients"
            emptyIcon={<PiUserListLight />}
            onItemRestored={() => fetchClients(sortBy, sortOrder, selectedFilters)}
            onBulkActionsChange={setTrashBulkActions}
            onSelectedCountChange={setTrashSelectedCount}
          />
        ) : (
        <div
          className="flex-grow overflow-y-auto custom-scrollbar-main flex flex-col"
          data-tour-id="clients-table"
        >
          {isAddingClient ? (
            <div className="flex-grow flex flex-col h-[100vh]">
              <ClientForm onCancel={() => history.push('/contacts')} onClientCreated={handleClientCreated} />
            </div>
          ) : (
            <div>
              {isClientsLoading && clients.length === 0 ? (
                // Show skeleton while loading
                <SkeletonClientItem viewMode="table" count={3} />
              ) : (
                // Show table with clients (Table handles empty state via emptyState prop)
                <Table
                  columns={columns}
                  data={tableData}
                  enableSelection={true}
                  selectedIds={selectedClients}
                  onSelect={(id) => handleSelectItem(String(id))}
                  onSelectAll={handleSelectAllItems}
                  onRowClick={(row) => history.push(`/contacts/${row.uuid}`)}
                  actions={[
                    {
                      label: t('Activity'),
                      onClick: (row) => setAuditTarget({ type: 'client', id: row.uuid || row.id, name: row.name || (typeof row['Client'] === 'string' ? row['Client'] : row['Client']?.name) || '' }),
                      icon: <PiClockCounterClockwiseLight className="h-4 w-4" />,
                    },
                    {
                      label: t('Delete'),
                      onClick: (row) => handleDeleteClient(row.id),
                      icon: <PiTrashLight className="h-4 w-4" />,
                    },
                  ]}
                  hasMore={hasMore}
                  isLoadingMore={isClientsLoading && page > 1}
                  onLoadMore={loadMoreClients}
                  sortableColumns={sortableColumns}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  total_items={totalClients}
                  total_showing_items={clients.length}
                  setIsShowing={true}
                  showingLabel={t('clients')}
                  selectedCount={selectedClients.length}
                  resizableColumns={true}
                  defaultColumnWidths={{
                    'Client': 280,
                    'Client Type': 180,
                    'Matters': 120,
                    'Is Onboarded': 160,
                    'Status': 140,
                    'Actions': 48,
                  }}
                  emptyState={{
                    icon: <PiUserListLight />,
                    headline: t('You have no clients added yet.'),
                    subtext: t('Start by adding client profiles to manage contacts, communications, and associated matters efficiently.'),
                    button: {
                      label: t('Add New Client'),
                      onClick: () => history.push('/contacts?add=true'),
                    },
                  }}
                />
              )}
            </div>
          )}
        </div>
        )}
      </div>
      <ImportClientsWizard
        isOpen={isImporting}
        onClose={() => setIsImporting(false)}
        onComplete={() => fetchClients()}
      />
      <FeatureTour
        steps={clientsListTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
      {auditTarget && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-20 z-40" onClick={() => setAuditTarget(null)} />
          <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-900 shadow-xl z-50 animate-slide-in-right border-l border-gray-200 dark:border-gray-700">
            <AuditLogPanel
              entityType={auditTarget.type}
              entityId={auditTarget.id}
              entityName={auditTarget.name}
              onClose={() => setAuditTarget(null)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ClientsList;
